import { createFormField } from '@ibsys/kit';
import { AlertTriangleIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { PointsRuleIssue } from '@/entities/expert-report';
import { cn } from '@/shared/lib';
import { AiTextarea } from '@/shared/ui';

/** Правило расчёта в двух формах: словами и формулой */
export type PointsRuleValue = {
  /** Формула, по которой считается результат */
  dsl: string;
  /** То же правило словами */
  dslHuman: string;
};

type Props = {
  value?: PointsRuleValue;
  onChange?: (value: PointsRuleValue) => void;
  /** Проблемы правила, найденные при последней проверке на сервере */
  issues?: PointsRuleIssue[];
  disabled?: boolean;
  /** Идёт сохранение: недостающую форму правила достраивает сервер */
  isProcessing?: boolean;
  className?: string;
};

const EMPTY_VALUE: PointsRuleValue = { dsl: '', dslHuman: '' };

/**
 * Правило расчёта результата блока с вложенными блоками или итога заключения.
 *
 * Обе формы правки — текст и формула — правятся как есть: перевод одной формы в другую делает
 * сервер при сохранении, поэтому отдельного запроса перевода тут нет (в отличие от `PredicateInput`).
 */
const PointsRuleInputBase: FC<Props> = ({ value, onChange, issues, disabled, isProcessing, className }) => {
  const { t } = useTranslation('expertReport');
  const rule = value ?? EMPTY_VALUE;

  const handleHumanChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.({ ...rule, dslHuman: event.target.value });
  };

  const handleDslChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.({ ...rule, dsl: event.target.value });
  };

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <AiTextarea
        label={t('pointsRule')}
        value={rule.dslHuman}
        placeholder={t('pointsRuleHumanPlaceholder')}
        disabled={disabled}
        isProcessing={isProcessing}
        onChange={handleHumanChange}
      />
      <AiTextarea
        label={t('pointsRuleDsl')}
        value={rule.dsl}
        placeholder={t('pointsRuleDslPlaceholder')}
        disabled={disabled}
        isProcessing={isProcessing}
        onChange={handleDslChange}
      />
      {issues && issues.length > 0 && (
        <div className="flex flex-col gap-2">
          {issues.map((issue) => (
            <div
              key={`${issue.code}-${issue.message}`}
              className="bg-warning/10 text-warning-foreground flex items-center gap-2 rounded-md p-2 text-sm"
            >
              <AlertTriangleIcon className="size-4 shrink-0" />
              <span>{issue.message}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

PointsRuleInputBase.displayName = 'PointsRuleInputBase';

export const PointsRuleInput = createFormField(PointsRuleInputBase);
